import React, { useEffect, useState, useMemo, useCallback } from 'react';
import Grid from '@mui/material/Grid';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
// import TableCell from '@mui/material/TableCell';
import TableCell from 'components/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Switch from '@mui/material/Switch';
import Collapse from '@mui/material/Collapse';
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';
import ArgonCurrencyInput from 'components/ArgonCurrencyInput';
import ArgonDatePicker from 'components/ArgonDatePicker';
import FormikNumberInput from 'components/FormikNumberInput';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import StripeTaxRateSelector from 'Routes/Organizer/Organization/Settings/Payments/StripeTaxRates/Selector';
import { getSystemFeeForProductType } from 'services/REST_API/orders';
import CustomRequiredFields from './CustomRequiredFields';
import Swal from 'sweetalert2';
import AdaptivePricingDialog from 'Routes/Organizer/Events/Details/EventInstances/EventInstanceDetails/components/AdaptivePricing/AdaptivePricingDialog';

const EventInstancePaymentSettings = React.memo(
  ({
    formik,
    event,
    paymentsEnabled,
    paymentsEnabledLoading,
    formMinWidth
  }) => {
    const { t, i18n } = useTranslation();

    const [calculatedSystemFee, setCalculatedSystemFee] = useState(0);

    const [openAdaptivePricingDialog, setOpenAdaptivePricingDialog] =
      useState(false);

    const handleOpenAdaptivePricingDialog = () => {
      setOpenAdaptivePricingDialog(true);
    };
    const handleCloseAdaptivePricingDialog = () => {
      setOpenAdaptivePricingDialog(false);
    };

    const handleSaveAdaptivePricing = (adaptiveRegistrationFeeData) => {
      console.log('handleSaveAdaptivePricing', adaptiveRegistrationFeeData);
      formik.setFieldValue(
        'adaptiveRegistrationFeeData',
        adaptiveRegistrationFeeData,
        false
      );
      setOpenAdaptivePricingDialog(false);
      // TODO: Save to database
    };

    const handleDatePickerChanged = useCallback(
      async (fieldName, newValue) => {
        if (newValue.length > 0) {
          await formik.setFieldValue(fieldName, newValue[0], false);
        }
      },
      [formik]
    );

    const calculateSystemFee = useCallback(
      async (registrationFee) => {
        console.log('Calculating system fee', {
          productAmount: registrationFee,
          productType: 'registration',
          subType: event?.type,
          organizationId: event?.organization?._id,
          eventInstanceId: formik?.values?._id
        });

        let calculatedSystemFee = 0;

        if (Number(registrationFee) > 0) {
          await getSystemFeeForProductType({
            productAmount: registrationFee * 100,
            productType: 'registration',
            subType: event?.type,
            organizationId: event?.organization?._id,
            eventInstanceId: formik?.values?._id
          })?.then((res) => {
            calculatedSystemFee = res?.systemFee / 100;
          });
        }

        console.log('Calculated system fee', calculatedSystemFee.toFixed(2));

        setCalculatedSystemFee(calculatedSystemFee.toFixed(2));
      },
      [event?.type, event?.organization?._id, formik?.values?._id]
    );

    const handleRegistrationFeeBlur = useCallback(() => {
      console.log(
        'handleRegistrationFeeBlur',

        formik.dirty
      );
      if (formik.dirty) {
        Swal.fire({
          icon: 'info',
          title: 'Registration Fee Updated',
          text: 'This field is only a default value for new divisions. Prices for existing divisions need to be changed in the division dialog below.',
          confirmButtonText: 'OK'
        });
      }
    }, [formik.dirty]);

    // Debounce system fee calculation
    useEffect(() => {
      const timeoutId = setTimeout(() => {
        calculateSystemFee(formik.values?.registrationFee);
      }, 500);

      return () => clearTimeout(timeoutId);
    }, [formik.values?.registrationFee, calculateSystemFee]);

    return (
      <Grid item xs={12} sm={12}>
        <Table>
          <TableHead></TableHead>
          <TableBody>
            <TableRow>
              <TableCell width="100%" padding={[1, 1, 1, 0.5]}>
                Payment Settings
              </TableCell>
              <TableCell
                width="100%"
                padding={[1, 1, 1, 0.5]}
                sx={{ minWidth: formMinWidth }}
              >
                {' '}
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell padding={[1, 1, 1, 0.5]}>
                <ArgonBox lineHeight={1.4}>
                  <ArgonTypography
                    display="block"
                    variant="caption"
                    fontWeight="bold"
                  >
                    Charge for registration
                  </ArgonTypography>
                  <Collapse in={!paymentsEnabled && !paymentsEnabledLoading}>
                    <ArgonBox mt={1} mb={1}>
                      <ArgonTypography
                        variant="caption"
                        color="warning"
                        fontWeight="bold"
                        display="flex"
                        alignItems="center"
                      >
                        <ErrorOutlineIcon fontSize="xs" />
                        &nbsp;You have not connected a stripe account to accept
                        payments. Go to&nbsp;
                        <u>
                          <Link
                            to="/organizer/organization/settings/payments"
                            style={{
                              textDecoration: 'undeline',
                              color: 'inherit'
                            }}
                          >
                            Setup / Payments & Shop
                          </Link>
                        </u>
                        &nbsp;to fix.
                      </ArgonTypography>
                    </ArgonBox>
                  </Collapse>
                  <ArgonTypography
                    variant="caption"
                    color="text"
                    fontWeight="regular"
                  >
                    <ArgonBox color="inherit" lineHeight="1.4" mt={1}>
                      Registrants will have to pay a registration fee to
                      register for this event. A platform fee will be applied to
                      the registration. It will be added to the registration fee
                      automatically.
                    </ArgonBox>
                  </ArgonTypography>
                </ArgonBox>
              </TableCell>
              <TableCell align="end" padding={[1, 1, 1, 0.5]}>
                <ArgonBox width={formMinWidth}>
                  <Switch
                    id="chargeForRegistration"
                    name="chargeForRegistration"
                    onChange={() => {
                      formik.setFieldValue(
                        'chargeForRegistration',
                        !formik.values.chargeForRegistration,
                        false
                      );
                    }}
                    checked={formik.values?.chargeForRegistration}
                    disabled={paymentsEnabledLoading}
                  />
                </ArgonBox>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <Collapse in={formik.values.chargeForRegistration}>
          <Table>
            {/* <TableHead></TableHead> */}
            <TableBody>
              <TableRow>
                <TableCell padding={[1, 1, 1, 0.5]}>
                  <ArgonBox lineHeight={1.4}>
                    <ArgonTypography
                      display="block"
                      variant="caption"
                      fontWeight="bold"
                    >
                      Applicable VAT rate
                    </ArgonTypography>
                    <ArgonTypography
                      variant="caption"
                      color="text"
                      fontWeight="regular"
                    >
                      <Collapse
                        in={!paymentsEnabled && !paymentsEnabledLoading}
                      >
                        <ArgonBox mt={1} mb={1}>
                          <ArgonTypography
                            variant="caption"
                            color="warning"
                            fontWeight="bold"
                            display="flex"
                            alignItems="center"
                          >
                            <ErrorOutlineIcon fontSize="xs" />
                            &nbsp;You have not connected a stripe account to
                            accept payments. Go to&nbsp;
                            <u>
                              <Link
                                to="/organizer/organization/settings/payments"
                                style={{
                                  textDecoration: 'undeline',
                                  color: 'inherit'
                                }}
                              >
                                Setup / Payments & Shop
                              </Link>
                            </u>
                            &nbsp;to fix.
                          </ArgonTypography>
                        </ArgonBox>
                      </Collapse>
                      <ArgonBox color="inherit" lineHeight="1.4" mt={1}>
                        Select / add the vat tax rate that applies to the
                        registration fees according to our local tax
                        requirements. Only Organization Admins can create tax
                        rates.
                      </ArgonBox>
                    </ArgonTypography>
                  </ArgonBox>
                </TableCell>
                <TableCell align="end" padding={[1, 1, 1, 0.5]}>
                  <ArgonBox width={formMinWidth}>
                    <StripeTaxRateSelector
                      disabled={!paymentsEnabled || paymentsEnabledLoading}
                      organization={event?.organization}
                      selectedTaxRateId={formik.values.stripeTaxRateId}
                      onChange={(taxRateOption) => {
                        formik.setFieldValue(
                          'stripeTaxRateId',
                          taxRateOption?.value,
                          false
                        );
                      }}
                      size="small"
                    />
                  </ArgonBox>
                </TableCell>
              </TableRow>

              {/* <TableRow>
              <TableCell padding={[1, 1, 1, 0.5]}>
                <ArgonBox lineHeight={1.4}>
                  <ArgonTypography
                    display="block"
                    variant="caption"
                    fontWeight="bold">
                    System fee
                  </ArgonTypography>
                  <ArgonTypography
                    variant="caption"
                    color="text"
                    fontWeight="regular">
                    This fee will be added to the registration fee and
                    automatically forwarded to{' '}
                    {process.env.REACT_APP_PLATTFORM_NAME}
                  </ArgonTypography>
                </ArgonBox>
              </TableCell>
              <TableCell align="start" padding={[1, 1, 1, 0.5]}>
                <ArgonBox width="100%">
                  <ArgonCurrencyInput
                    id="systemFee"
                    name="systemFee"
                    size="small"
                    intlConfig={{
                      locale: i18n.language,
                      currency: event.currency || 'EUR'
                    }}
                    disabled
                    allowNegativeValue={false}
                    placeholder=""
                    defaultValue={0}
                    decimalsLimit={2}
                    value={calculatedSystemFee || 0}
                    error={
                      formik.touched.registrationFee &&
                      Boolean(formik.errors.registrationFee)
                    }
                  />
                </ArgonBox>
              </TableCell>
            </TableRow> */}
            </TableBody>
          </Table>
        </Collapse>
      </Grid>
    );
  }
);

EventInstancePaymentSettings.displayName = 'Payment Settings';

export default EventInstancePaymentSettings;
